import axios from "axios";
import { toastManager } from "../toastManager";
import { extractErrorMessage } from "../utils/extractErrorMessage";

const axiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (response) => {
    if(response)
    {
      const credentialResponse = JSON.parse(localStorage.getItem('token'));
      if (credentialResponse) {
        response.headers['Authorization'] = `Bearer ${credentialResponse}`;
      }
      return response;
    }
},
(error) => { 
  return Promise.reject(error);
}
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // On 401, clear the session and redirect to login; surface all other
    // errors as a toast. Either way reject so callers hit their error paths.
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      window.location.replace("/login");
    } else if (!error.config?.suppressErrorToast) {
      // Callers that render their own error UI can pass { suppressErrorToast: true }
      // in the request config to opt out of the global toast.
      toastManager.error(extractErrorMessage(error));
    }
    return Promise.reject(error);
  }
);

export const useApiRequests = () => {
  const getAllUsers = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/users`);
      return response;
    } catch (error) {
    }
  };

  const getAllUsersPaginated = async (pageNumber) => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/users/userpage/${pageNumber}`);
      return response;
    } catch (error) {
    }
  };

  const getUsersWithCategorySums = async (categoryId = null, year = null) => {
    try {
      const params = new URLSearchParams();
      if (categoryId != null) params.append("categoryId", categoryId);
      if (year != null) params.append("year", year);
      const queryString = params.toString();
      const url = queryString
        ? `${process.env.REACT_APP_API_KEY}/users/withcategorysums?${queryString}`
        : `${process.env.REACT_APP_API_KEY}/users/withcategorysums`;
      const response = await axiosInstance.get(url);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const getAdminHistoryLog = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/adminactionhistorylog`);
      return response;
    } catch (error) {
    }
  };

  const getUserById = async (id) => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/users/getkudos/${id}`);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const getUserByEmail = async (email, config) => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/Users/getuser/${email}`, config);
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  const getUserHistoryLogById = async (id) => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/usershistorylog/${id}`);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const getAllUserInformation = async (id) => {
    try {
      const [userMainInfoResponse, userHistoryLogResponse] = await Promise.all([getUserById(id), getUserHistoryLogById(id)]);
      const UsersInformation = userMainInfoResponse.data;
      const HistoryLogResponse = userHistoryLogResponse.data;
      return { UsersInformation, HistoryLogResponse };
    } catch (error) {
      console.error('Error fetching user information:', error.message);
      return error;
    }
  };

  const getAllProducts = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/shopitems`);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };


  const getNonHiddenProducts = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/shopitems/nonhiddenproducts`);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const getAllRequests = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/requests`);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const getMyRequests = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/requests/my`);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const getAllShoppingPageInformation = async () => {
    try {
      const [usersResponse, productsResponse] = await Promise.all([getAllUsers(), getNonHiddenProducts()]);
      const allUsersInformation = usersResponse.data;
      const allNonHiddenShoppingItemsInformation = productsResponse.data;
      return { allUsersInformation, allNonHiddenShoppingItemsInformation };
      //add here map
    } catch (error) {
      console.error(error);
      return error;
    }
  };


  const postRegisterPurchase = async (productId, comment) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_KEY}/registerpurchase`,
        {
          productId: productId,
          comment: comment,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const postSendKudosFromAdminToEmployee = async (userId, kudosAmount, transactionReason) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_KEY}/kudosfromvisma`,
        {
          userId: userId,
          kudosAmount: Number(kudosAmount),
          reason: transactionReason,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const postSendKudosFromEmployeeToEmployee = async (receiverId, amountOfKudos, transactionReason) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_KEY}/persontopersontransaction`,
        {
          receiverId: receiverId,
          kudosAmount: Number(amountOfKudos),
          reason: transactionReason,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const postAddNewUser = async (userFullName) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_KEY}/users/add`,
        {
          fullName: userFullName
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const deleteUserById = async (userId) => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_KEY}/users/delete?userId=${userId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const editUserById = async (userId, newUserFullName) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_KEY}/users/edit`,
        {
          userId: userId,
          fullName: newUserFullName
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const postAddNewProducs = async (newProductName, newProductPrice, newProductComment, isNewProductAvailable) => {
    try {
      const response = await axiosInstance
        .post(
          `${process.env.REACT_APP_API_KEY}/shopitems/add`,
          {
            productId: 0,
            productName: newProductName,
            productPrice: newProductPrice,
            productComment: newProductComment,
            productIsAvailable: isNewProductAvailable,
          },
          {
            headers: {
              "content-type": "text/json",
            },
          }
        )
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const editProduct = async (productId, newName, newPrice, newComment, newAvailability) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_KEY}/shopitems/edit`,
        {
          id: productId,
          productName: newName,
          productPrice: newPrice,
          productComment: newComment,
          productIsAvailable: newAvailability,
        }
      );
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  const deleteProductById = async (productId) => {
    try {
      const response = await axiosInstance.delete(`${process.env.REACT_APP_API_KEY}/ShopItems/delete?productId=${productId}`);
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  // Pre-check product deletion dependencies
  const preCheckDeleteProduct = async (productId) => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/ShopItems/delete/precheck?productId=${productId}`);
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  // Delete product with action type (0 = Archive, 1 = Delete)
  const deleteProductAction = async (productId, actionType) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_KEY}/ShopItems/delete/action`,
        { id: productId, actionType: actionType }
      );
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  const approveRequestByTypeAndId = async (requestTypeId, id, adminNotes = null) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_KEY}/requests/approve/${requestTypeId}/${id}`,
        { adminNotes: adminNotes }
      );
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  }

  const rejectRequestByTypeAndId = async (requestTypeId, id, adminNotes = null) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_KEY}/requests/reject/${requestTypeId}/${id}`,
        { adminNotes: adminNotes }
      );
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  }

  const requestKudos = async (selectedEmployee, selectedKudosType, selectedKudosTypeValue, reason) => {
    try {
      const response = await axiosInstance.post(`${process.env.REACT_APP_API_KEY}/requestkudos`, {
        UserIdTo: selectedEmployee.id,
        selectedKudosType: selectedKudosType,
        requestKudosTypeValueId: selectedKudosTypeValue.id,
        kudosAmount: selectedKudosTypeValue.value,
        reason: reason
      });
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  const exportData = async (categoryId = null, year = null, categoryName = null) => {
    try {
      // Forward the active category/year filter so the export mirrors the
      // filtered view instead of always dumping all users' total kudos.
      const params = new URLSearchParams();
      if (categoryId != null) params.append("categoryId", categoryId);
      if (year != null) params.append("year", year);
      const queryString = params.toString();
      const response = await axiosInstance({
        url: queryString
          ? `${process.env.REACT_APP_API_KEY}/importexport?${queryString}`
          : `${process.env.REACT_APP_API_KEY}/importexport`,
        method: "GET",
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      // File name reflects the active filter: category (or "all") and year.
      const safeCategory = categoryName
        ? categoryName.replace(/[^\w-]/g, "")
        : "all";
      const fileName = `KudoShopInfo_${safeCategory}${year != null ? `_${year}` : ""}.csv`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.log(error.message);
    }
  };

  const getAllRequestKudosTypes = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/requestkudostypes`);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const getRequestKudosTypesById = async (typeId) => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/requestkudostypes/${typeId}`);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const postRequestKudosTypes = async (typeName) => {
    try {
      const response = await axiosInstance.post(`${process.env.REACT_APP_API_KEY}/Requestkudostypes`, {
        name: typeName
      });
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  }

  const putRequestKudosType = async (requestKudosType) => {
    try {
      const response = await axiosInstance.put(`${process.env.REACT_APP_API_KEY}/Requestkudostypes`, {
        id: requestKudosType.id,
        name: requestKudosType.name,
      });
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  }

  const deleteRequestKudosType = async (kudosTypeId) => {
    try {
      const response = await axiosInstance.delete(`${process.env.REACT_APP_API_KEY}/Requestkudostypes/${kudosTypeId}`);
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  }

  const getAllRequestKudosTypesValues = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/requestkudostypesvalues`);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const getRequestKudosTypesValuesById = async (typeValueId) => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/requestkudostypesvalues/${typeValueId}`);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const getRequestKudosTypesValuesByRequestKudosTypeId = async (requestKudosTypeId) => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/requestkudostypesvalues/byRequestKudosTypeId/${requestKudosTypeId}`);
      return response;
    } catch (error) {
      console.log(error.message);
    }
  };

  const postRequestKudosTypeValue = async (valueName, value, typeId) => {
    try {
      const response = await axiosInstance.post(`${process.env.REACT_APP_API_KEY}/requestkudostypesvalues`, {
        name: valueName,
        value: value,
        requestKudosTypeId: typeId
      });
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  }

  const putRequestKudosTypeValue = async (requestKudosTypeValue) => {
    try {
      const response = await axiosInstance.put(`${process.env.REACT_APP_API_KEY}/requestkudostypesvalues`, {
        id: requestKudosTypeValue.id,
        name: requestKudosTypeValue.name,
        value: requestKudosTypeValue.value,
        isAvailable: requestKudosTypeValue.isAvailable,
        requestKudosTypeId: requestKudosTypeValue.requestKudosTypeId,
      });
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  }

  const deleteRequestKudosTypeValue = async (kudosTypeValueId) => {
    try {
      const response = await axiosInstance.delete(`${process.env.REACT_APP_API_KEY}/requestkudostypesvalues/${kudosTypeValueId}`);
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  }

  // Pre-check kudos type value deletion dependencies
  const preCheckDeleteKudosTypeValue = async (valueId) => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/requestkudostypesvalues/delete/precheck/${valueId}`);
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  // Delete kudos type value with action type (0 = Archive, 1 = Delete)
  const deleteKudosTypeValueAction = async (valueId, actionType) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_KEY}/requestkudostypesvalues/delete/action`,
        { id: valueId, actionType: actionType }
      );
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  const uploadFile = async (file, productId) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_KEY}/filestorage/products/${encodeURIComponent(productId)}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  const deleteFile = async (blobName) => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_KEY}/filestorage/${blobName}`
      );
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  const downloadFile = async (blobName) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_KEY}/filestorage/${blobName}`,
        {
          responseType: 'blob',
        }
      );

      // Return blob URL that can be used in img src
      return URL.createObjectURL(new Blob([response.data]));
    } catch (error) {
      console.log('Error downloading file:', error.message);
      return Promise.reject(error);
    }
  };

  const getAllFilesAsMetadata = async (productId) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_KEY}/filestorage/products/${productId}`
      );

      // Return the metadata array directly
      return response.data;
    } catch (error) {
      console.log('Error fetching file metadata:', error.message);
      return Promise.reject(error);
    }
  };

  const getFileUrl = (blobName) => {
    return `${process.env.REACT_APP_API_KEY}/filestorage/${blobName}`;
  };

  const importUsers = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_KEY}/excelImport/users`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  const getCart = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_KEY}/cart`);
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  const addToCart = async (productId, comment) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_KEY}/cart/items`,
        { productId: productId, comment: comment ?? null }
      );
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_KEY}/cart/items/${productId}`
      );
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  const checkoutCart = async () => {
    try {
      // Per-item comments are captured at add-to-cart time; checkout takes no body.
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_KEY}/cart/checkout`
      );
      return response;
    } catch (error) {
      console.log(error.message);
      return Promise.reject(error);
    }
  };

  return {
    getAllUsers,
    getAllUsersPaginated,
    getUsersWithCategorySums,
    getAdminHistoryLog,
    getAllUserInformation,
    getUserByEmail,
    getAllShoppingPageInformation,
    postRegisterPurchase,
    postSendKudosFromAdminToEmployee,
    postSendKudosFromEmployeeToEmployee,
    postAddNewUser,
    getAllProducts,
    getNonHiddenProducts,
    getAllRequests,
    getMyRequests,
    postAddNewProducs,
    deleteUserById,
    editProduct,
    deleteProductById,
    preCheckDeleteProduct,
    deleteProductAction,
    approveRequestByTypeAndId,
    rejectRequestByTypeAndId,
    requestKudos,
    editUserById,
    exportData,
    getAllRequestKudosTypes,
    getRequestKudosTypesById,
    getAllRequestKudosTypesValues,
    getRequestKudosTypesValuesById,
    getRequestKudosTypesValuesByRequestKudosTypeId,
    postRequestKudosTypes,
    putRequestKudosType,
    postRequestKudosTypeValue,
    deleteRequestKudosType,
    putRequestKudosTypeValue,
    deleteRequestKudosTypeValue,
    preCheckDeleteKudosTypeValue,
    deleteKudosTypeValueAction,
    uploadFile,
    deleteFile,
    downloadFile,
    getAllFilesAsMetadata,
    getFileUrl,
    importUsers,
    getCart,
    addToCart,
    removeFromCart,
    checkoutCart,
  };
};
